import { ChampionSelectSession } from '../../domain/lobby/championSelect.js';
import { MAP_CONFIGS } from '../../domain/maps.js';
import { MP_TEST_MAP_ID, orderMapsForLobby } from '../../domain/map/mpTestDefaults.js';
import { LobbyController } from './lobbyController.js';
import { bootMinimap } from './bootMinimap.js';
import { applyLobbyRunes } from './applyLobbyRunes.js';
import { applyLobbyMasteries } from './applyLobbyMasteries.js';
import { createMpClient } from '../../application/net/mpClient.js';
import { bindMatchBrowserMenuFromDocument } from './matchBrowserMenuController.js';
import { bindWaitingRoomFromDocument } from './waitingRoomController.js';
import {
  beginMpLiveMatch,
  hideMatchBrowserOverlays
} from './beginMpLiveMatch.js';
import { createMpPresenceSession } from './wireMpPresence.js';
import { resolveClientMaps } from './productClientGate.js';
import { wireLobbyLoadoutPersistence } from './wireLobbyLoadoutPersistence.js';
import { resolveMpWsUrl } from '../net/resolveMpWsUrl.js';

const DEFAULT_MP_URL = resolveMpWsUrl(import.meta.env);

function toLockInPayload(matchConfig) {
  return {
    championId: matchConfig.championId,
    spell1: matchConfig.spells?.[0],
    spell2: matchConfig.spells?.[1],
    runePage: matchConfig.runePage,
    masteryPage: matchConfig.masteryPage
  };
}

/**
 * bootLobbyAndMinimap — Match browser + lobby + AR-06 minimap + MP remotes.
 */
export function bootLobbyAndMinimap({
  rootEl,
  canvasEl,
  matchRuntime,
  mapObjectives,
  mapBounds,
  switchChampion,
  loadLiveMap,
  applyLobbySpells,
  playerEntity,
  playerInventory,
  mpPresenceDeps,
  search
} = {}) {
  const minimap = bootMinimap({
    rootEl,
    canvasEl,
    matchRuntime,
    mapObjectives,
    mapBounds
  });
  minimap.setMap(matchRuntime.mapId);

  let mpClient = null;
  let liveStarted = false;
  const presence = mpPresenceDeps
    ? createMpPresenceSession({
      ...mpPresenceDeps,
      getPlayerEntity: () => playerEntity
    })
    : null;

  const clientSearch = search !== undefined && search !== null
    ? search
    : (typeof location !== 'undefined' ? location.search : '');
  const clientMaps = resolveClientMaps(
    orderMapsForLobby(Object.keys(MAP_CONFIGS)),
    clientSearch
  );
  const lobbySession = new ChampionSelectSession({
    availableMaps: clientMaps,
    selectedMapId: clientMaps[0] || MP_TEST_MAP_ID
  });
  const loadoutPersistence = wireLobbyLoadoutPersistence({ session: lobbySession });
  const lobbyController = new LobbyController(lobbySession, (matchConfig) => {
    if (matchConfig && matchConfig.championId) {
      switchChampion(matchConfig.championId);
    }
    if (matchConfig && matchConfig.mapId) {
      loadLiveMap(matchConfig.mapId);
      minimap.setMap(matchConfig.mapId);
    }
    applyLobbySpells(matchConfig);
    applyLobbyRunes(matchConfig, playerEntity, playerInventory);
    applyLobbyMasteries(matchConfig, playerEntity);
  }, {
    onLoadoutPersist: () => loadoutPersistence.persist()
  });
  lobbyController.init();

  function enterLive(match, client) {
    if (liveStarted || !match) return;
    liveStarted = true;
    beginMpLiveMatch({
      match,
      localClientId: client.getClientId(),
      hideOverlays: () => hideMatchBrowserOverlays(document),
      switchChampion,
      loadLiveMap: (mapId) => {
        loadLiveMap(mapId);
        minimap.setMap(mapId);
      },
      applyLobbySpells,
      applyLobbyRunes,
      applyLobbyMasteries,
      playerEntity,
      playerInventory,
      fallback: lobbySession.getMatchConfig()
    });
    if (presence) presence.attach(client, match);
    if (typeof window !== 'undefined') {
      window.__debugMatchBrowser = {
        ...(window.__debugMatchBrowser || {}),
        match,
        live: true,
        remotes: presence ? presence.remotes.getRemotes().size : 0
      };
    }
  }

  function enableMpLoadout(client) {
    mpClient = client;
    lobbyController.onMpLockIn = (matchConfig) => {
      client.lockIn(toLockInPayload(matchConfig));
      const status = document.getElementById('mb-status');
      if (status) {
        status.textContent = `Loadout fijado: ${matchConfig.championId}`;
      }
    };
  }

  const matchBrowser = bindMatchBrowserMenuFromDocument(document, {
    maps: lobbySession.availableMaps,
    createClient: () => createMpClient({
      url: DEFAULT_MP_URL,
      displayName: `Player-${Math.floor(Math.random() * 900 + 100)}`,
      openSocket: (u) => new WebSocket(u || DEFAULT_MP_URL)
    }),
    onSandbox: () => {
      lobbyController.onMpLockIn = null;
      if (presence) presence.detach();
      lobbyController.showLobby();
    },
    onRoomReady: ({ room, client }) => {
      liveStarted = false;
      if (presence) presence.detach();
      enableMpLoadout(client);
      if (typeof window !== 'undefined') {
        window.__debugMatchBrowser = {
          room,
          client,
          getRoomState: () => client.getRoomState(),
          getClientId: () => client.getClientId()
        };
      }
      const waitingRoom = bindWaitingRoomFromDocument(document, {
        client,
        onOpenLoadout: () => {
          lobbyController.showLobby();
        },
        onMatchStart: (match) => enterLive(match, client)
      });
      waitingRoom.showRoom(room);
      client.waitForMatchStart()
        .then((match) => enterLive(match, client))
        .catch(() => {});
    }
  });

  return {
    minimap,
    lobbySession,
    lobbyController,
    matchBrowser,
    getMpClient: () => mpClient,
    tickMpPresence: () => {
      if (presence) presence.tick();
    }
  };
}
